import React, { useEffect, useState } from 'react';
import {
  IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonInput, IonItem, IonLabel, IonList, IonNote,
  IonSelect, IonSelectOption,
} from '@ionic/react';
import { ServiceCustomer, ServiceSettingsBundle, TaxLocation } from '../../../interfaces/ServiceEstimate';
import { serviceApi } from '../../../utils/serviceEstimatesApi';
import './ServiceSettingsAdmin.css';

interface Props {
  bundle: ServiceSettingsBundle;
  onChanged: () => void;
  notify: (msg: string) => void;
}

type LocRow = Partial<TaxLocation> & { ratePct: string };

/** Admin and Director only: tax locations, proposal settings and new customers from the field. */
const ServiceSettingsAdmin: React.FC<Props> = ({ bundle, onChanged, notify }) => {
  const [locs, setLocs] = useState<LocRow[]>([]);
  const [validDays, setValidDays] = useState(String(bundle.settings.proposal_valid_days));
  const [freightPct, setFreightPct] = useState(String(Math.round(Number(bundle.settings.default_freight_rate) * 1e6) / 1e4));
  const [laborCost, setLaborCost] = useState(String(Number(bundle.settings.internal_labor_cost)));
  const [reviewTotal, setReviewTotal] = useState(String(Number(bundle.settings.review_total ?? 10000)));
  const [reviewMarginPct, setReviewMarginPct] = useState(String(Math.round(Number(bundle.settings.review_margin ?? 0.2) * 1000) / 10));
  const thisYear = new Date().getFullYear();
  const [acsLastOutside, setAcsLastOutside] = useState(
    bundle.settings.acs_last_outside_year === thisYear && bundle.settings.acs_last_outside ? String(bundle.settings.acs_last_outside).padStart(4, '0') : '');
  const [pending, setPending] = useState<ServiceCustomer[]>([]);
  const [mergeQ, setMergeQ] = useState<Record<string, string>>({});
  const [mergeHits, setMergeHits] = useState<Record<string, ServiceCustomer[]>>({});

  const load = () => {
    serviceApi.allTaxLocations().then((all) => setLocs(all.filter((l) => l.active).map((l) => ({ ...l, ratePct: String(Math.round(Number(l.rate) * 1e5) / 1e3) })))).catch(() => setLocs([]));
    serviceApi.pendingCustomers().then(setPending).catch(() => setPending([]));
  };
  useEffect(load, []);

  const saveLoc = async (row: LocRow, i: number) => {
    try {
      const saved = await serviceApi.saveTaxLocation({ id: row.id, state: row.state || 'IL', county: row.county || '', city: row.city || '', rate: String(Number(row.ratePct) / 100) });
      setLocs(locs.map((l, j) => (j === i ? { ...saved, ratePct: row.ratePct } : l)));
      notify(`Saved ${saved.label}`);
      onChanged();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Could not save the location.');
    }
  };

  const removeLoc = async (row: LocRow, i: number) => {
    if (row.id) {
      try {
        await serviceApi.removeTaxLocation(row.id);
      } catch (e) {
        notify(e instanceof Error ? e.message : 'Could not remove the location.');
        return;
      }
    }
    setLocs(locs.filter((_, j) => j !== i));
    onChanged();
  };

  const saveSettings = async () => {
    const days = Math.round(Number(validDays));
    if (!(days > 0)) { notify('Enter the number of days a proposal is valid.'); return; }
    const margin = Number(reviewMarginPct);
    if (!(margin >= 0 && margin < 100)) { notify('Enter the review margin as a percent, e.g. 20.'); return; }
    const last = acsLastOutside.trim() ? Number(acsLastOutside) : 0;
    if (!(Number.isInteger(last) && last >= 0 && last <= 9999)) { notify('Enter the last ACS# as four digits, e.g. 0412.'); return; }
    try {
      await serviceApi.saveSettings({
        proposal_valid_days: days, default_freight_rate: String(Number(freightPct) / 100), internal_labor_cost: String(Number(laborCost)),
        review_total: String(Number(reviewTotal) || 0), review_margin: String(margin / 100),
        acs_last_outside: last, acs_last_outside_year: last ? thisYear : null,
      });
      notify('Settings saved');
      onChanged();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Could not save settings.');
    }
  };

  const findMerge = async (id: string, q: string) => {
    setMergeQ({ ...mergeQ, [id]: q });
    if (q.trim().length < 2) return;
    const hits = await serviceApi.searchCustomers(q.trim()).catch(() => [] as ServiceCustomer[]);
    setMergeHits({ ...mergeHits, [id]: hits.filter((h) => h.id !== id) });
  };

  const setRow = (i: number, p: Partial<LocRow>) => setLocs(locs.map((l, j) => (j === i ? { ...l, ...p } : l)));

  return (
    <div className="service-admin">
      <IonCard>
        <IonCardHeader><IonCardTitle>Tax locations</IonCardTitle></IonCardHeader>
        <IonCardContent>
          <p className="service-admin-note">The choices in the Job location picker. Rates come from Alpha&apos;s accountant. Changing a rate doesn&apos;t change estimates already priced.</p>
          {locs.map((l, i) => (
            <div className="service-admin-loc" key={l.id ?? `new-${i}`}>
              <IonItem>
                <IonSelect label="State" labelPlacement="stacked" value={l.state || 'IL'} onIonChange={(e) => setRow(i, { state: e.detail.value })}>
                  <IonSelectOption value="IL">IL</IonSelectOption>
                  <IonSelectOption value="WI">WI</IonSelectOption>
                </IonSelect>
              </IonItem>
              <IonItem><IonInput label="County" labelPlacement="stacked" value={l.county} onIonInput={(e) => setRow(i, { county: String(e.detail.value ?? '') })} /></IonItem>
              <IonItem><IonInput label="City (blank = rest of county)" labelPlacement="stacked" value={l.city} onIonInput={(e) => setRow(i, { city: String(e.detail.value ?? '') })} /></IonItem>
              <IonItem><IonInput label="Combined rate (%)" labelPlacement="stacked" inputmode="decimal" value={l.ratePct} onIonInput={(e) => setRow(i, { ratePct: String(e.detail.value ?? '') })} /></IonItem>
              <div className="service-admin-loc-actions">
                <IonButton size="small" onClick={() => saveLoc(l, i)}>Save</IonButton>
                <IonButton size="small" fill="clear" color="medium" onClick={() => removeLoc(l, i)}>Remove</IonButton>
              </div>
            </div>
          ))}
          <IonButton fill="outline" size="small" onClick={() => setLocs([...locs, { state: 'IL', county: '', city: '', ratePct: '' }])}>+ Add location</IonButton>
        </IonCardContent>
      </IonCard>

      <IonCard>
        <IonCardHeader><IonCardTitle>Review and proposal settings</IonCardTitle></IonCardHeader>
        <IonCardContent>
          <div className="service-admin-grid">
            <IonItem><IonInput label="Office review when total is at least ($)" labelPlacement="stacked" inputmode="decimal" value={reviewTotal} onIonInput={(e) => setReviewTotal(String(e.detail.value ?? ''))} /></IonItem>
            <IonItem><IonInput label="Office review when margin is below (%)" labelPlacement="stacked" inputmode="decimal" value={reviewMarginPct} onIonInput={(e) => setReviewMarginPct(String(e.detail.value ?? ''))} /></IonItem>
            <IonItem><IonInput label={`Last ACS# issued outside this app in ${thisYear} (four digits)`} labelPlacement="stacked" inputmode="numeric" placeholder="e.g. 0412" value={acsLastOutside} onIonInput={(e) => setAcsLastOutside(String(e.detail.value ?? ''))} /></IonItem>
            <IonItem><IonInput label="Proposal valid for (days)" labelPlacement="stacked" inputmode="numeric" value={validDays} onIonInput={(e) => setValidDays(String(e.detail.value ?? ''))} /></IonItem>
            <IonItem><IonInput label="Default freight (%)" labelPlacement="stacked" inputmode="decimal" value={freightPct} onIonInput={(e) => setFreightPct(String(e.detail.value ?? ''))} /></IonItem>
            <IonItem><IonInput label="Internal labor cost ($/hr)" labelPlacement="stacked" inputmode="decimal" value={laborCost} onIonInput={(e) => setLaborCost(String(e.detail.value ?? ''))} /></IonItem>
          </div>
          <p className="service-admin-note">Markup bands and labor rates can be changed in Django admin under Service estimates.</p>
          <IonButton onClick={saveSettings}>Save settings</IonButton>
        </IonCardContent>
      </IonCard>

      <IonCard>
        <IonCardHeader><IonCardTitle>New customers from the field</IonCardTitle></IonCardHeader>
        <IonCardContent>
          {pending.length === 0 && <p className="service-admin-note">Nothing to review.</p>}
          <IonList>
            {pending.map((c) => (
              <div className="service-admin-pending" key={c.id}>
                <IonLabel>
                  <h3>{c.name}</h3>
                  <p>{[c.address, c.city, c.state, c.zip_code].filter(Boolean).join(', ')}{c.contact_email ? ` · ${c.contact_email}` : ''}</p>
                </IonLabel>
                <div className="service-admin-loc-actions">
                  <IonButton size="small" onClick={async () => { await serviceApi.approveCustomer(c.id); notify(`Approved ${c.name}`); load(); }}>Approve</IonButton>
                  <IonInput className="service-admin-merge" placeholder="Duplicate of… (search)" value={mergeQ[c.id] || ''} onIonInput={(e) => findMerge(c.id, String(e.detail.value ?? ''))} />
                </div>
                {(mergeHits[c.id] || []).map((h) => (
                  <IonItem key={h.id} lines="none">
                    <IonLabel>{h.name}<IonNote> · {[h.city, h.state].filter(Boolean).join(', ')}</IonNote></IonLabel>
                    <IonButton slot="end" size="small" fill="outline" onClick={async () => { await serviceApi.mergeCustomer(c.id, h.id); notify(`Merged into ${h.name}`); load(); }}>Merge into this</IonButton>
                  </IonItem>
                ))}
              </div>
            ))}
          </IonList>
        </IonCardContent>
      </IonCard>
    </div>
  );
};

export default ServiceSettingsAdmin;
